import { useAuth0 } from '@auth0/auth0-vue'
import { useQuery } from '@tanstack/vue-query'
import { ApiError } from '@thyme/libs/src/api/apiRequest'
import { HTTPStatusCode } from '@thyme/libs/src/api/types/statusCodes'
import { hoursToMs } from '@thyme/libs/src/time/convert'
import { MaybeRef, Ref, computed, toValue, watch } from 'vue'
import {
  Entity,
  EntityRole,
  MapEntitiesLicensedState,
} from '@/legacy/types/entities/entities'
import { NotificationActions } from '@/legacy/types/notifications'
import { showErrorNotification } from '@/libs/api/notifications'
import { setupBaseMutation } from '@/libs/query/mutations'
import { useFlagStore } from '@/store/flags/flags'
import { whoAmIApi } from './api'

const WHO_AM_I_STALE_HOURS = 2

const whoAmIQueryKeys = {
  all: ['whoami'] as const,
  me: (email: MaybeRef<string | undefined>) =>
    [...whoAmIQueryKeys.all, email] as const,
}

/**
 *
 */
function useWhoAmICore() {
  const auth = useAuth0()
  const email = computed(() => auth.user.value?.email)
  const flagStore = useFlagStore()

  const query = useQuery({
    // Query key includes email so it automatically re-runs on auth changes and is disabled on logout.
    queryKey: whoAmIQueryKeys.me(email),
    queryFn: () => whoAmIApi.retrieve({}),
    staleTime: hoursToMs(WHO_AM_I_STALE_HOURS),
    gcTime: hoursToMs(WHO_AM_I_STALE_HOURS),
    enabled: computed(() => !!toValue(email) && flagStore.isLoaded),
  })

  return {
    query,
    email,
  }
}

/**
 * Returns convenient refs with the current user's profile information.
 */
export function useWhoAmI(): {
  selfEntity: Ref<Entity | null>
  selfEntityId: Ref<string | null>
  roles: Ref<EntityRole[]>
  permissions: Ref<string[]>
  licensedStates: Ref<MapEntitiesLicensedState | null>
} {
  const { query } = useWhoAmICore()

  // Convenience wrappers.
  const selfEntity = computed(() => query.data.value?.entity ?? null)
  const selfEntityId = computed(() => selfEntity.value?.entityId ?? null)
  const roles = computed(() => query.data.value?.roles ?? [])
  const permissions = computed(() => query.data.value?.permissions ?? [])
  const licensedStates = computed(
    () => query.data.value?.licensedStates ?? null
  )

  return { selfEntity, selfEntityId, roles, permissions, licensedStates }
}

const useCreateSelfEntity = setupBaseMutation({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- email needed for cache invalidation.
  mutationFn: (_email: string | undefined) => whoAmIApi.create({}),
  cacheStrategy: {
    setDataFromResponseQueryKeys: [(_, email) => whoAmIQueryKeys.me(email)],
  },
  notifications: {
    operation: NotificationActions.CREATE,
    resourceName: 'user profile',
  },
})

/**
 * Bootstraps the whoAmI query and auto-provisions the user on 404.
 * Call once from App.vue setup.
 */
export function useWhoAmIInitialization(): {
  isReady: Ref<boolean>
} {
  const { query, email } = useWhoAmICore()
  const createSelfEntity = useCreateSelfEntity()

  // Auto-create user on 404; show notification for other errors.
  watch(query.isError, (isError) => {
    if (!isError) return
    const err = query.error.value
    if (
      err instanceof ApiError &&
      err.status === HTTPStatusCode.ERROR_404_NOT_FOUND.valueOf()
    ) {
      console.log("User's self info not found; creating...")
      createSelfEntity.mutate(email.value)
    } else {
      showErrorNotification(err, {
        operation: NotificationActions.FETCH,
        resourceName: 'user profile',
      })
    }
  })

  const isReady = computed(() => !!query.data.value?.entity)
  return { isReady }
}
